import { ref, markRaw } from 'vue'
import { supabase } from '../services/supabase.js'
import { getTokenFromURL } from '../utils/tokenHandler.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import TimeSavedWidget from '../components/TimeSavedWidget.vue'
import UpcomingBookingsWidget from '../components/UpcomingBookingsWidget.vue'
import UpcomingToursWidget from '../components/UpcomingToursWidget.vue'
import PlansStartingSoonWidget from '../components/PlansStartingSoonWidget.vue'
import PlansEndingSoonWidget from '../components/PlansEndingSoonWidget.vue'
import AssignmentsEndingSoonWidget from '../components/AssignmentsEndingSoonWidget.vue'
import AssignmentsStartingSoonWidget from '../components/AssignmentsStartingSoonWidget.vue'
import CustomAutomationsWidget from '../components/CustomAutomationsWidget.vue'
import RecentAutomationEnrollmentsWidget from '../components/RecentAutomationEnrollmentsWidget.vue'
import MostPopularAutomationsWidget from '../components/MostPopularAutomationsWidget.vue'
import AutomationEnrolmentsWidget from '../components/AutomationEnrolmentsWidget.vue'
import CheckInsWidget from '../components/CheckInsWidget.vue'
import CheckedInWidget from '../components/CheckedInWidget.vue'
import ActionsThisPeriodWidget from '../components/ActionsThisPeriodWidget.vue'
import RevenueCollectedWidget from '../components/RevenueCollectedWidget.vue'
import UnpaidInvoicesWidget from '../components/UnpaidInvoicesWidget.vue'
import InvoicesWidget from '../components/InvoicesWidget.vue'
import TopAccountsByRevenueWidget from '../components/TopAccountsByRevenueWidget.vue'
import PlanRevenueWidget from '../components/PlanRevenueWidget.vue'
import TopBookersWidget from '../components/TopBookersWidget.vue'
import TasksWidget from '../components/TasksWidget.vue'
import LatestMessagesWidget from '../components/LatestMessagesWidget.vue'
import ManualVsAutomatedWidget from '../components/ManualVsAutomatedWidget.vue'
import CompletedToursWidget from '../components/CompletedToursWidget.vue'
import ConvertedLeadsFromToursWidget from '../components/ConvertedLeadsFromToursWidget.vue'
import ConversionRateFromToursWidget from '../components/ConversionRateFromToursWidget.vue'
import ConvertedLeadsFromInquiriesWidget from '../components/ConvertedLeadsFromInquiriesWidget.vue'
import ConversionRateFromInquiriesWidget from '../components/ConversionRateFromInquiriesWidget.vue'
import ConvertedLeadsFromDropinsWidget from '../components/ConvertedLeadsFromDropinsWidget.vue'
import ConversionRateFromDropinsWidget from '../components/ConversionRateFromDropinsWidget.vue'
import TourPassPlanPurchaseWidget from '../components/TourPassPlanPurchaseWidget.vue'
import DropinRepeatBookingWidget from '../components/DropinRepeatBookingWidget.vue'
import DropinPassPlanPurchaseWidget from '../components/DropinPassPlanPurchaseWidget.vue'
import CreditOwedWidget from '../components/CreditOwedWidget.vue'
import PublicGroupConversationsWidget from '../components/PublicGroupConversationsWidget.vue'
import SupportChatWidget from '../components/SupportChatWidget.vue'
/**
 * Widget component mapping
 * Maps widget IDs to their Vue components
 */
const WIDGET_COMPONENTS = {
  'time-saved': TimeSavedWidget,
  'upcoming-bookings': UpcomingBookingsWidget,
  'upcoming-tours': UpcomingToursWidget,
  'completed-tours': CompletedToursWidget,
  'plans-starting-soon': PlansStartingSoonWidget,
  'plans-ending-soon': PlansEndingSoonWidget,
  'assignments-ending-soon': AssignmentsEndingSoonWidget,
  'assignments-starting-soon': AssignmentsStartingSoonWidget,
  'custom-automations': CustomAutomationsWidget,
  'recent-automation-enrollments': RecentAutomationEnrollmentsWidget,
  'most-popular-automations': MostPopularAutomationsWidget,
  'automation-enrolments': AutomationEnrolmentsWidget,
  'check-ins': CheckInsWidget,
  'checked-in': CheckedInWidget,
  'actions-this-period': ActionsThisPeriodWidget,
  'invoices-paid': RevenueCollectedWidget, // id kept for back-compat with saved dashboards
  'unpaid-invoices': UnpaidInvoicesWidget,
  'invoices': InvoicesWidget,
  'top-accounts-by-revenue': TopAccountsByRevenueWidget,
  'plan-revenue': PlanRevenueWidget,
  'top-bookers': TopBookersWidget,
  'tasks': TasksWidget,
  'latest-messages': LatestMessagesWidget,
  'manual-vs-automated': ManualVsAutomatedWidget,
  'converted-leads-from-tours': ConvertedLeadsFromToursWidget,
  'conversion-rate-from-tours': ConversionRateFromToursWidget,
  'converted-leads-from-inquiries': ConvertedLeadsFromInquiriesWidget,
  'conversion-rate-from-inquiries': ConversionRateFromInquiriesWidget,
  'converted-leads-from-dropins': ConvertedLeadsFromDropinsWidget,
  'conversion-rate-from-dropins': ConversionRateFromDropinsWidget,
  'tour-pass-plan-purchase': TourPassPlanPurchaseWidget,
  'dropin-repeat-booking': DropinRepeatBookingWidget,
  'dropin-pass-plan-purchase': DropinPassPlanPurchaseWidget,
  'credit-owed': CreditOwedWidget,
  'public-group-conversations': PublicGroupConversationsWidget,
  'support-chat': SupportChatWidget
}

/**
 * Get the Optix token from URL
 * @returns {string|null} Token or null if not found
 */
function getOptixToken() {
  return getTokenFromURL()
}

/**
 * Serialize sections for storage (removes Vue components, keeps only IDs)
 * @param {Array} sections - Array of section objects with widgets
 * @returns {Array} Serialized sections without Vue components
 */
function serializeSections(sections) {
  return sections.map((section, index) => ({
    id: section.id,
    title: section.title,
    expanded: section.expanded,
    displayMode: section.displayMode || 'detailed',
    position: index, // Save section position/order
    widgets: (section.widgets || []).map(widget => ({
      id: widget.id,
      colSpan: widget.colSpan || 1,
      rowSpan: widget.rowSpan || 1,
      props: widget.props || {}
      // Note: component is not stored, it will be restored from WIDGET_COMPONENTS
    }))
  }))
}

/**
 * Deserialize sections from storage (restores Vue components from IDs)
 * @param {Array} serializedSections - Array of serialized section objects
 * @returns {Array} Sections with restored Vue components, sorted by position
 */
function deserializeSections(serializedSections) {
  // Sort sections by position if available, otherwise maintain order
  const sortedSections = [...serializedSections].sort((a, b) => {
    const posA = a.position !== undefined ? a.position : 0
    const posB = b.position !== undefined ? b.position : 0
    return posA - posB
  })
  
  return sortedSections.map(section => ({
    id: section.id,
    title: section.title,
    expanded: section.expanded !== undefined ? section.expanded : true,
    displayMode: section.displayMode || 'detailed',
    widgets: (section.widgets || []).map(widget => {
      // Try to find component by widget ID first
      let component = WIDGET_COMPONENTS[widget.id]
      
      // If not found, try extracting base widget ID (remove timestamp suffix if present)
      // Widget IDs with timestamps look like: "upcoming-bookings-1234567890" or "latest-messages-1771550173520-0"
      if (!component && widget.id.includes('-')) {
        // Strip numeric suffix(es): "latest-messages-1771550173520-0" -> "latest-messages"
        const baseFromSuffix = widget.id.replace(/-\d+(-\d+)*$/, '')
        if (WIDGET_COMPONENTS[baseFromSuffix]) {
          component = WIDGET_COMPONENTS[baseFromSuffix]
        }
        // Fallback: prefix match (longest base IDs first)
        if (!component) {
          const baseIds = Object.keys(WIDGET_COMPONENTS).sort((a, b) => b.length - a.length)
          for (const baseId of baseIds) {
            if (widget.id === baseId || widget.id.startsWith(baseId + '-')) {
              component = WIDGET_COMPONENTS[baseId]
              break
            }
          }
        }
        // Fallback: try progressively shorter base IDs from split
        if (!component) {
          const parts = widget.id.split('-')
          for (let i = parts.length - 1; i > 0; i--) {
            const baseWidgetId = parts.slice(0, i).join('-')
            if (WIDGET_COMPONENTS[baseWidgetId]) {
              component = WIDGET_COMPONENTS[baseWidgetId]
              break
            }
          }
        }
      }
      
      if (!component) {
        console.warn(`Widget component not found for ID: ${widget.id}`)
        return null
      }
      return {
        id: widget.id,
        component: markRaw(component),
        colSpan: widget.colSpan || 1,
        rowSpan: widget.rowSpan || 1,
        props: widget.props || {}
      }
    }).filter(Boolean) // Remove any null entries
  }))
}

/**
 * Composable for managing dashboard storage
 */
export function useDashboardStorage() {
  const isLoading = ref(false)
  const isSaving = ref(false)
  const error = ref(null)

  /**
   * Load dashboard configuration via Edge Function
   * Edge Function validates token and retrieves IDs server-side
   * @returns {Promise<Array|null>} Array of sections or null if not found
   */
  const loadDashboard = async () => {
    try {
      isLoading.value = true
      error.value = null

      const token = getOptixToken()
      if (!token) {
        console.warn('⚠️ No token found. Using default dashboard.')
        return { sections: null, llm_enabled: false, managers_can_view_revenue: true }
      }


      // Call Edge Function which validates token and loads dashboard server-side
      const { data, error: functionError } = await supabase.functions.invoke('get-dashboard', {
        body: { token },
      })

      if (functionError) {
        console.error('❌ Edge Function error:', functionError)
        // Check if it's a 429 rate limit error
        if (isRateLimitError(functionError)) {
          error.value = {
            message: 'Too many requests. Please retry after 30 seconds',
            isRateLimit: true
          }
        } else {
          error.value = functionError.message
        }
        return { sections: null, llm_enabled: false, managers_can_view_revenue: true }
      }

      if (!data) {
        return { sections: null, llm_enabled: false, managers_can_view_revenue: true }
      }

      // Deserialize sections to restore Vue components
      const sections = data.sections ? deserializeSections(data.sections) : null
      return {
        sections,
        llm_enabled: data.llm_enabled === true,
        // Default to true (visible) when the org has no setting yet
        managers_can_view_revenue: data.managers_can_view_revenue !== false
      }
    } catch (err) {
      console.error('Error loading dashboard:', err)
      // Check if it's a 429 rate limit error
      if (isRateLimitError(err)) {
        error.value = {
          message: 'Too many requests. Please retry after 30 seconds',
          isRateLimit: true
        }
      } else {
        error.value = err.message
      }
      return { sections: null, llm_enabled: false, managers_can_view_revenue: true }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Save dashboard configuration via Edge Function
   * Edge Function validates token and saves dashboard server-side
   * @param {Array} sections - Array of section objects with widgets
   * @returns {Promise<boolean>} True if successful
   */
  const saveDashboard = async (sections, llmEnabled = false) => {
    try {
      isSaving.value = true
      error.value = null

      const token = getOptixToken()
      if (!token) {
        console.warn('⚠️ No token found. Dashboard changes will not be saved.')
        return false
      }

      // Serialize sections (remove Vue components)
      const serializedSections = serializeSections(sections)
      
      // Log summary of what's being saved
      const totalWidgets = serializedSections.reduce((sum, section) => sum + (section.widgets?.length || 0), 0)

      // Call Edge Function which validates token and saves dashboard server-side
      const body = {
        token,
        sections: serializedSections,
        llm_enabled: llmEnabled
      }

      const invokeWithRetry = async () => {
        const { data, error: functionError } = await supabase.functions.invoke('save-dashboard', { body })
        if (functionError) {
          // Extract diagnostic info from FunctionsHttpError (context is the Response)
          let errDetails = functionError.message
          const status = functionError.context?.status
          if (functionError.context) {
            try {
              const resBody = await functionError.context.json()
              if (resBody?.optixStatus) errDetails += ` (Optix status: ${resBody.optixStatus})`
              if (resBody?.optixError) errDetails += ` - ${JSON.stringify(resBody.optixError)}`
              if (resBody?.optixErrors) errDetails += ` - ${JSON.stringify(resBody.optixErrors)}`
              console.error('❌ Edge Function error details:', { status, ...resBody })
            } catch {
              // Body may not be JSON or response already consumed
            }
          }
          return { error: functionError, details: errDetails, status }
        }
        return { data }
      }

      let result = await invokeWithRetry()
      if (result.error && result.status === 401) {
        // Retry once after 2s (transient Optix token/rate issues)
        console.warn('⚠️ Save failed with 401, retrying in 2s...')
        await new Promise(r => setTimeout(r, 2000))
        result = await invokeWithRetry()
      }

      if (result.error) {
        console.error('❌ Edge Function error:', result.error, result.details)
        error.value = result.details || result.error.message
        return false
      }

      return true
    } catch (err) {
      console.error('❌ Error saving dashboard:', err)
      error.value = err.message
      return false
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Save an org-level setting via Edge Function.
   * The Edge Function validates the token and enforces OWNER server-side.
   * @param {boolean} managersCanViewRevenue - Whether managers can view revenue widgets
   * @returns {Promise<boolean>} True if successful
   */
  const saveOrgSettings = async (managersCanViewRevenue) => {
    try {
      isSaving.value = true
      error.value = null

      const token = getOptixToken()
      if (!token) {
        console.warn('⚠️ No token found. Org settings will not be saved.')
        return false
      }

      const { error: functionError } = await supabase.functions.invoke('save-org-settings', {
        body: { token, managers_can_view_revenue: managersCanViewRevenue }
      })

      if (functionError) {
        console.error('❌ save-org-settings error:', functionError)
        error.value = functionError.message
        return false
      }

      return true
    } catch (err) {
      console.error('❌ Error saving org settings:', err)
      error.value = err.message
      return false
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Delete dashboard configuration (not implemented - would need delete-dashboard Edge Function)
   * @returns {Promise<boolean>} True if successful
   */
  const deleteDashboard = async () => {
    console.warn('Delete dashboard not implemented - would require delete-dashboard Edge Function')
    return false
  }

  return {
    isLoading,
    isSaving,
    error,
    loadDashboard,
    saveDashboard,
    saveOrgSettings,
    deleteDashboard
  }
}
